import { useState, useEffect } from 'react';

// Polls GET /api/ai-status every 5s + on window focus. Backend's
// utils/aiMutex.js answers with the current state of sort / mcq / check
// slots. Each pipeline is independent (sort, MCQ, and check can all run
// at once). Sort + MCQ are exclusive within themselves; check is concurrent
// (active/max). Consumers: AnalyzeTab (sort), MCQsOMR (mcq), AssessmentTab
// auto-check (check.active vs check.max so only N browsers grade at once).
//
// Stays unauthenticated — the backend route is public on purpose so
// the poll runs even before login completes (the banner just shows
// nothing until the user is in).
const emptySlot = () => ({
  busy: false, source: null, folderPath: null, label: null, startedAt: null
});
const emptyCheck = () => ({
  ...emptySlot(), active: 0, max: 0
});

export default function useAiStatus(pollIntervalMs = 5000) {
  const [status, setStatus] = useState({
    sort: emptySlot(),
    mcq: emptySlot(),
    ocr: emptySlot(),
    grading: emptySlot(),
    check: emptyCheck(),
    local: null,
    hostSplit: false,
    dispatch: null
  });

  useEffect(() => {
    let cancelled = false;
    const apiBase = window.API_BASE_URL || '';
    // Strip the trailing /api if present so we hit /api/ai-status. The
    // API_BASE_URL convention in this app already includes /api.
    const url = `${apiBase.replace(/\/api\/?$/, '/api')}/ai-status`;

    async function pull() {
      try {
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setStatus({
          sort: data.sort || emptySlot(),
          mcq: data.mcq || emptySlot(),
          ocr: data.ocr || emptySlot(),
          grading: data.grading || emptySlot(),
          check: {
            ...emptyCheck(),
            ...(data.check || {})
          },
          local: data.local || null,
          hostSplit: !!data.hostSplit,
          dispatch: data.dispatch || null
        });
      } catch (_) {
        // Silent — banner just stays in its last state if the server
        // is briefly unreachable.
      }
    }

    pull();
    const handle = setInterval(pull, pollIntervalMs);
    const onFocus = () => pull();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(handle);
      window.removeEventListener('focus', onFocus);
    };
  }, [pollIntervalMs]);

  return status;
}
